import { JourneyStore } from '@yard/stores/journey.store'
import { YardBlockStore } from '@yard/stores/YardBlockStore'
import _ from 'lodash'
import { action, computed, makeObservable, observable } from 'mobx'
import { mapYardIssueItemToYardIssueDto } from '../helpers/issue.mapper'
import { YardIssueDto, YardIssueStatus, YardIssueTypeDummy } from '../models/yard-issue.model'
import { YardIssueStore } from './yard-issue.store'

export class YardIssueUIStore {
  issueFilterStatus?: YardIssueStatus
  issueFilterType?: YardIssueTypeDummy
  containerNumberFilter? = ''
  carrierNameFilter? = ''
  isFilterEnabled = false

  constructor(
    private readonly issueStore: YardIssueStore,
    private readonly journeyStore: JourneyStore,
    private readonly yardBlockStore: YardBlockStore,
  ) {
    makeObservable(this, {
      issueFilterStatus: observable,
      issueFilterType: observable,
      containerNumberFilter: observable,
      carrierNameFilter: observable,
      isFilterEnabled: observable,

      fetch: action,
      setContainerNumberFilter: action,
      setIssueFilterStatus: action,
      setIssueFilterType: action,
      setCarrierNameFilter: action,
      enableFilters: action,

      issues: computed,
      dataFilteredByIssueStatus: computed,
      issueTypes: computed,
      filteredData: computed,
      issuesTypeCount: computed,
      unresolvedIssues: computed,
    })
  }

  async fetch() {
    await this.journeyStore.loadOpenJourneys()
    await this.issueStore.load()
    await this.yardBlockStore.loadYardBlocks()
  }

  get issues(): YardIssueDto[] {
    return this.issueStore.items.map(issue =>
      mapYardIssueItemToYardIssueDto(
        issue,
        this.journeyStore.journeys,
        this.yardBlockStore.yardBlocks,
      ),
    )
  }

  setContainerNumberFilter(filter?: string) {
    this.containerNumberFilter = filter
  }

  setIssueFilterStatus(issueFilterStatus?: string) {
    if (this.issueFilterStatus !== issueFilterStatus) {
      this.issueFilterStatus = issueFilterStatus as YardIssueStatus
    }
  }

  setIssueFilterType(issueFilterType?: string) {
    if (this.issueFilterType !== issueFilterType) {
      this.issueFilterType = issueFilterType as YardIssueTypeDummy
    }
  }

  setCarrierNameFilter(filter?: string) {
    this.carrierNameFilter = filter
  }

  enableFilters(apply: boolean) {
    this.isFilterEnabled = apply
  }

  get dataFilteredByIssueStatus() {
    const data = this.filteredData ?? []

    return data.filter(
      i =>
        (!this.issueFilterStatus || i.issueStatus === this.issueFilterStatus) &&
        (!this.issueFilterType || i.issueType === this.issueFilterType),
    )
  }

  get issueTypes(): string[] {
    return _(this.filteredData)
      .map(i => i.issueType)
      .filter(i => !!i)
      .uniq()
      .value()
  }

  get filteredData(): YardIssueDto[] {
    let filteredData = this.unresolvedIssues

    if (this.containerNumberFilter) {
      filteredData = filteredData.filter(i =>
        i.containerNumbers.some(cn => cn.includes(this.containerNumberFilter!)),
      )
    }

    return filteredData
  }

  get issuesTypeCount() {
    const items = this.filteredData
    let unresolved = 0
    let followUp = 0

    items.forEach(item => {
      if (item.issueStatus === YardIssueStatus.Unresolved) {
        unresolved++
      } else if (item.issueStatus === YardIssueStatus.FollowUp) {
        followUp++
      }
    })

    return {
      all: items.length,
      unresolved,
      followUp,
    }
  }

  get unresolvedIssues(): YardIssueDto[] {
    return this.issues.filter(i => i.issueStatus !== YardIssueStatus.Resolved)
  }

  clearFilters() {
    this.setCarrierNameFilter()
    this.setContainerNumberFilter()
    this.enableFilters(false)
    this.setIssueFilterStatus()
    this.setIssueFilterType()
  }
}
